import { FieldArray, Form, Formik } from 'formik';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { LuFileText } from 'react-icons/lu';
import * as Yup from 'yup';
import settingsApi from '../../../APIs/settings';
import { useRoot } from '../../../context/RootProvider';
import {
  BrowseFile,
  Button,
  FormContainer,
  Input,
  Typography,
} from '../../../designComponents';
import { FileWithKeys, requestAPI, uploadFilesToS3 } from '../../../utils';
const validationSchema = Yup.object().shape({
  licenses: Yup.array()
    .of(
      Yup.object().shape({
        licenseName: Yup.string().required('License Name is required'),
        licenseNumber: Yup.string().required('License Number is required'),
        issuingAuthority: Yup.string().required(
          'Issuing Authority is required'
        ),
        expiryDate: Yup.string().required('Expiry Date is required'),
        img: Yup.array()
          .min(1, 'Upload at least one license image')
          .required('License image is required'),
      })
    )
    .min(1, 'At least one license is required'),

  certifications: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required('Certification Name is required'),
        issuer: Yup.string().required('Issuer is required'),
        year: Yup.number()
          .typeError('Year must be a number')
          .required('Year is required'),
        pdf: Yup.array()
          .min(1, 'Upload at least one certificate PDF')
          .required('Certificate PDF is required'),
      })
    )
    .min(1, 'At least one certification is required'),
});
const isPdf = (url: string) =>
  typeof url === 'string' && url.toLowerCase().includes('.pdf');

const FilePreview = ({ file, alt }: { file: string; alt: string }) => {
  if (isPdf(file)) {
    return (
      <a
        href={file}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '80px',
          height: '80px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          textDecoration: 'none',
          color: '#555',
          fontSize: '11px',
          gap: '4px',
          background: '#f9f9f9',
        }}
      >
        <LuFileText size={28} color="#e74c3c" />
        <span>PDF</span>
      </a>
    );
  }

  return (
    <a href={file} target="_blank" rel="noopener noreferrer">
      <img
        src={file}
        alt={alt}
        style={{
          width: '80px',
          height: '80px',
          objectFit: 'cover',
          borderRadius: '4px',
        }}
        onError={(e: any) => {
          e.target.src = '/assets/icons/file-icon.png';
        }}
      />
    </a>
  );
};

const TechnicianDocuments = () => {
  const { updateAuth, auth } = useRoot();

  const handleDelete = async (
    type: 'License' | 'Certification',
    licenseNumber?: string,
    name?: string
  ) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this ${type.toLowerCase()}?`
      )
    )
      return;

    try {
      const updatedProfile = await requestAPI(
        type === 'License'
          ? settingsApi.deleteCertification({
              documentType: 'License',
              licenseNumber,
            })
          : settingsApi.deleteCertification({
              documentType: 'Certification',
              name,
            })
      );

      updateAuth({ ...updatedProfile });
      toast(`${type} deleted successfully`);
    } catch (err) {
      console.error(err);
      toast(`Failed to delete ${type.toLowerCase()}`);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Licenses */}
      <div style={{ marginBottom: '40px' }}>
        <Typography size={16} strong style={{ marginBottom: '10px' }}>
          Licenses
        </Typography>

        {auth?.licenses?.length ? (
          auth.licenses.map((license: any, idx: number) => (
            <div
              key={idx}
              style={{
                border: '1px solid #ccc',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                }}
              >
                <Typography strong>{license.licenseName}</Typography>
                <button
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'red',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    handleDelete(
                      'License',
                      license.licenseNumber,
                      license.licenseNumber
                    )
                  }
                >
                  Delete
                </button>
              </div>

              <Typography>Number: {license.licenseNumber}</Typography>
              <Typography>Issued by: {license.issuingAuthority}</Typography>
              <Typography>
                Expiry: {new Date(license.expiryDate).toLocaleDateString()}
              </Typography>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                {license.img?.map((file: string, fileIdx: number) => (
                  <FilePreview
                    key={fileIdx}
                    file={file}
                    alt={`license-${fileIdx}`}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          <Typography>No licenses uploaded.</Typography>
        )}
      </div>

      {/* Certifications */}
      <div>
        <Typography size={16} strong style={{ marginBottom: '10px' }}>
          Certifications
        </Typography>

        {auth?.certifications?.length ? (
          auth.certifications.map((cert: any, idx: number) => (
            <div
              key={idx}
              style={{
                border: '1px solid #ccc',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                }}
              >
                <Typography strong>{cert.name}</Typography>
                <button
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'red',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    handleDelete('Certification', cert.name, cert.name)
                  }
                >
                  Delete
                </button>
              </div>

              <Typography>Issuer: {cert.issuer}</Typography>
              <Typography>Year: {cert.year}</Typography>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                {cert.pdf?.map((file: string, fileIdx: number) => (
                  <FilePreview
                    key={fileIdx}
                    file={file}
                    alt={`cert-${fileIdx}`}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          <Typography>No certifications uploaded.</Typography>
        )}
      </div>
    </div>
  );
};

const MAX_FILES = 1;

const LicensesAndCertifications = () => {
  const { updateAuth, auth } = useRoot();
  const [addMode, setAddMode] = useState(false);
  const [loading, setLoading] = useState(false);

  return (
    <>
      <Button
        onClick={() => setAddMode(!addMode)}
        style={{ marginBottom: '10px' }}
      >
        {!addMode
          ? 'Add Certificates and license'
          : 'View Certificates and license'}
      </Button>

      {!addMode && <TechnicianDocuments />}

      {addMode && (
        <Formik
          initialValues={{
            licenses: [
              {
                licenseName: '',
                licenseNumber: '',
                issuingAuthority: '',
                expiryDate: '',
                img: [],
              },
            ],
            certifications: [
              {
                name: '',
                issuer: '',
                year: '',
                pdf: [],
              },
            ],
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            (async () => {
              try {
                setLoading(true);

                const processFiles = async (
                  items: any[],
                  fileField: string
                ) => {
                  const result: any[] = [];

                  for (const item of items || []) {
                    const newFiles: File[] = [];
                    const existingKeys: string[] = [];

                    (item[fileField] || []).forEach((file: File | string) => {
                      // @ts-ignore
                      if (file instanceof File) newFiles.push(file);
                      if (typeof file === 'string') existingKeys.push(file);
                    });

                    let uploadedKeys: string[] = [];
                    if (newFiles.length > 0) {
                      const uploaded = (await uploadFilesToS3(
                        newFiles
                      )) as FileWithKeys[];
                      uploadedKeys = uploaded.map((f) => f.key);
                    }

                    result.push({
                      ...item,
                      [fileField]: [...existingKeys, ...uploadedKeys],
                    });
                  }

                  return result;
                };

                const certificationsPayload = await processFiles(
                  values.certifications || [],
                  'pdf'
                );
                const licensesPayload = await processFiles(
                  values.licenses || [],
                  'img'
                );

                const payload = {
                  certifications: [
                    ...(auth?.certifications ?? []),
                    ...certificationsPayload,
                  ],
                  licenses: [...(auth?.licenses ?? []), ...licensesPayload],
                };

                const updatedData = await requestAPI(
                  settingsApi.updateTechnicianDocuments(payload)
                );

                if (updatedData) {
                  updateAuth({ ...updatedData });
                  toast('Certifications and License added successfully');
                  setAddMode(false);
                }
              } catch (error) {
                console.error(error);
                toast('There was a problem, Try again later');
              } finally {
                setLoading(false);
              }
            })();
          }}
        >
          {({ values, setFieldValue, touched, errors }) => (
            <Form>
              <Typography strong size={14} style={{ marginBottom: 16 }}>
                Licenses
              </Typography>

              <FieldArray name="licenses">
                {({ push, remove }) => (
                  <>
                    {values.licenses.map((_, index) => (
                      <div key={index} className="card-box">
                        <FormContainer
                          label="License Name"
                          error={
                            touched.licenses?.[index]?.licenseName &&
                            //@ts-ignore
                            errors.licenses?.[index]?.licenseName
                          }
                        >
                          <Input
                            value={values.licenses[index].licenseName}
                            onChange={(e) =>
                              setFieldValue(
                                `licenses.${index}.licenseName`,
                                e.target.value
                              )
                            }
                          />
                        </FormContainer>

                        <FormContainer
                          label="License Number"
                          error={
                            touched.licenses?.[index]?.licenseNumber &&
                            //@ts-ignore

                            errors.licenses?.[index]?.licenseNumber
                          }
                        >
                          <Input
                            value={values.licenses[index].licenseNumber}
                            onChange={(e) =>
                              setFieldValue(
                                `licenses.${index}.licenseNumber`,
                                e.target.value
                              )
                            }
                          />
                        </FormContainer>

                        <FormContainer
                          label="Issuing Authority"
                          error={
                            touched.licenses?.[index]?.issuingAuthority &&
                            //@ts-ignore

                            errors.licenses?.[index]?.issuingAuthority
                          }
                        >
                          <Input
                            value={values.licenses[index].issuingAuthority}
                            onChange={(e) =>
                              setFieldValue(
                                `licenses.${index}.issuingAuthority`,
                                e.target.value
                              )
                            }
                          />
                        </FormContainer>

                        <FormContainer
                          label="Expiry Date"
                          error={
                            touched.licenses?.[index]?.expiryDate &&
                            //@ts-ignore

                            errors.licenses?.[index]?.expiryDate
                          }
                        >
                          <Input
                            type="date"
                            value={values.licenses[index].expiryDate}
                            onChange={(e) =>
                              setFieldValue(
                                `licenses.${index}.expiryDate`,
                                e.target.value
                              )
                            }
                          />
                        </FormContainer>

                        <FormContainer
                          label="Upload License Image"
                          error={
                            touched.licenses?.[index]?.img &&
                            //@ts-ignore

                            errors.licenses?.[index]?.img
                          }
                        >
                          <BrowseFile
                            accepts=".jpg,.png,.jpeg,.pdf"
                            multiple={false}
                            files={values.licenses[index].img}
                            setFiles={(files: File[]) =>
                              setFieldValue(
                                `licenses.${index}.img`,
                                files.slice(0, MAX_FILES)
                              )
                            }
                          />
                        </FormContainer>

                        {values.licenses.length > 1 && (
                          <Button
                            variant="danger"
                            onClick={() => remove(index)}
                          >
                            Remove License
                          </Button>
                        )}
                      </div>
                    ))}

                    <Button
                      variant="gray"
                      onClick={() =>
                        push({
                          licenseName: '',
                          licenseNumber: '',
                          issuingAuthority: '',
                          expiryDate: '',
                          img: [],
                        })
                      }
                    >
                      Add License
                    </Button>
                  </>
                )}
              </FieldArray>

              <Typography strong size={14} style={{ margin: '32px 0 16px' }}>
                Certifications
              </Typography>

              <FieldArray name="certifications">
                {({ push, remove }) => (
                  <>
                    {values.certifications.map((_, index) => (
                      <div key={index} className="card-box">
                        <FormContainer
                          label="Certification Name"
                          error={
                            touched.certifications?.[index]?.name &&
                            //@ts-ignore

                            errors.certifications?.[index]?.name
                          }
                        >
                          <Input
                            value={values.certifications[index].name}
                            onChange={(e) =>
                              setFieldValue(
                                `certifications.${index}.name`,
                                e.target.value
                              )
                            }
                          />
                        </FormContainer>

                        <FormContainer
                          label="Issuer"
                          error={
                            touched.certifications?.[index]?.issuer &&
                            //@ts-ignore
                            errors.certifications?.[index]?.issuer
                          }
                        >
                          <Input
                            value={values.certifications[index].issuer}
                            onChange={(e) =>
                              setFieldValue(
                                `certifications.${index}.issuer`,
                                e.target.value
                              )
                            }
                          />
                        </FormContainer>

                        <FormContainer
                          label="Year"
                          error={
                            touched.certifications?.[index]?.year &&
                            //@ts-ignore

                            errors.certifications?.[index]?.year
                          }
                        >
                          <Input
                            type="number"
                            value={values.certifications[index].year}
                            onChange={(e) =>
                              setFieldValue(
                                `certifications.${index}.year`,
                                e.target.value
                              )
                            }
                          />
                        </FormContainer>

                        <FormContainer
                          label="Upload Certificate (PDF)"
                          error={
                            touched.certifications?.[index]?.pdf &&
                            //@ts-ignore

                            errors.certifications?.[index]?.pdf
                          }
                        >
                          <BrowseFile
                            accepts=".pdf"
                            multiple={false}
                            files={values.certifications[index].pdf}
                            setFiles={(files: File[]) =>
                              setFieldValue(
                                `certifications.${index}.pdf`,
                                files.slice(0, MAX_FILES)
                              )
                            }
                          />
                        </FormContainer>

                        {values.certifications.length > 1 && (
                          <Button
                            variant="danger"
                            onClick={() => remove(index)}
                          >
                            Remove Certification
                          </Button>
                        )}
                      </div>
                    ))}

                    <Button
                      variant="gray"
                      onClick={() =>
                        push({
                          name: '',
                          issuer: '',
                          year: '',
                          pdf: [],
                        })
                      }
                    >
                      Add Certification
                    </Button>
                  </>
                )}
              </FieldArray>

              <div style={{ marginTop: 32 }}>
                <Button isLoading={loading} isDisabled={loading} type="submit">
                  Save
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default LicensesAndCertifications;
